import { CoreApiClient } from "./coreApiClient";

const API_BASE_URL = (() => {
  const envValue = import.meta.env.VITE_API_BASE_URL;
  if (envValue && envValue.trim() !== "") {
    return envValue.trim();
  }
  return "/api";
})();

const SESSION_STORAGE_KEY = "pm.referralPortal.session.v1";

export type ReferralPortalSession = {
  email: string;
  clientId: number | null;
  clientName: string | null;
  phone: string | null;
  isAdmin: boolean;
  expiresAt: string;
  token: string;
};

export type ReferralPortalRequestRow = {
  referralLeadId: number;
  requestCode: string | null;
  submittedDate: string | null;
  referralName: string | null;
  referralEmail: string | null;
  referralPhone: string | null;
  clientName: string | null;
  clientEmail: string | null;
  projectAddress: string | null;
  customerType?: string | null;
  helpType?: string | null;
  suburb: string | null;
  postCode: number | null;
  council: string | null;
  reviewStatus: string | null;
  referralStatus: string | null;
  quotationId: number | null;
  quotationNo: string | null;
  quotationStatus: string | null;
  projectId: number | null;
  projectNo: string | null;
  portalUserEmail?: string | null;
};

export type ReferralPortalAdminDashboard = {
  summary: {
    totalRequests: number;
    pendingReviewCount: number;
    duplicateReviewCount: number;
    quotedCount: number;
    projectCount: number;
  };
  rows: ReferralPortalRequestRow[];
};

type ApiEnvelope<T> = {
  success: true;
  data: T;
  meta?: Record<string, unknown>;
};

type RequestAccessPayload = {
  email: string;
  fullName?: string;
  phone?: string;
  company?: string;
  notes?: string;
};

type QuoteRequestPayload = {
  clientFirstName: string;
  clientLastName?: string;
  clientPhone: string;
  clientEmail: string;
  projectAddress: string;
  customerType?: string | null;
  helpType?: string | null;
  suburb?: string;
  postCode?: number | null;
  council?: string;
  notes?: string;
  uploadedFiles: Array<{
    fileId: string;
    fileName: string;
    contentType: string | null;
    size: number;
  }>;
};

const coreClient = new CoreApiClient(API_BASE_URL, (input, init) => fetch(input, init));

function readStoredSession(): ReferralPortalSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as ReferralPortalSession;
    if (!parsed?.token || !parsed?.email || !parsed?.expiresAt) {
      return null;
    }

    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return {
      ...parsed,
      phone: parsed.phone || null,
      isAdmin: Boolean(parsed.isAdmin),
    };
  } catch {
    return null;
  }
}

function writeStoredSession(session: ReferralPortalSession | null) {
  if (typeof window === "undefined") {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

function authHeaders() {
  const session = readStoredSession();
  return session ? { Authorization: `Bearer ${session.token}` } : {};
}

export const referralPortalClient = {
  getSession() {
    return readStoredSession();
  },

  setSession(session: ReferralPortalSession | null) {
    writeStoredSession(session);
  },

  logout() {
    writeStoredSession(null);
  },

  async requestAccess(payload: RequestAccessPayload) {
    const response = await coreClient.request<ApiEnvelope<{
      accessRequestId: number;
      email: string;
      status: string;
      message: string;
    }>>(`${API_BASE_URL}/v1/public/portal-auth/request-access`, {
      method: "POST",
      body: payload,
    });
    return response.data;
  },

  async requestLoginCode(email: string) {
    const response = await coreClient.request<ApiEnvelope<{
      email: string;
      clientName: string | null;
      expiresAt: string;
      deliveryMode: string;
    }>>(`${API_BASE_URL}/v1/public/portal-auth/request-code`, {
      method: "POST",
      body: { email },
    });
    return response.data;
  },

  async verifyLoginCode(email: string, code: string) {
    const response = await coreClient.request<ApiEnvelope<{ session: ReferralPortalSession }>>(
      `${API_BASE_URL}/v1/public/portal-auth/verify-code`,
      {
        method: "POST",
        body: { email, code },
      }
    );
    writeStoredSession(response.data.session);
    return response.data.session;
  },

  async getMe() {
    const response = await coreClient.request<ApiEnvelope<{ session: ReferralPortalSession }>>(
      `${API_BASE_URL}/v1/public/me`,
      {
        method: "GET",
        headers: authHeaders(),
      }
    );
    writeStoredSession(response.data.session);
    return response.data.session;
  },

  async listRequests() {
    const response = await coreClient.request<ApiEnvelope<ReferralPortalRequestRow[]>>(
      `${API_BASE_URL}/v1/public/quote-requests`,
      {
        method: "GET",
        headers: authHeaders(),
      }
    );
    return response.data;
  },

  async getAdminDashboard() {
    const response = await coreClient.request<ApiEnvelope<ReferralPortalAdminDashboard>>(
      `${API_BASE_URL}/v1/public/admin/dashboard`,
      {
        method: "GET",
        headers: authHeaders(),
      }
    );
    return response.data;
  },

  async checkDuplicate(payload: Omit<QuoteRequestPayload, "uploadedFiles">) {
    const response = await coreClient.request<ApiEnvelope<{
      decision: "allow" | "review" | "block";
      matches: Array<{
        referralLeadId: number;
        referralCode: string | null;
        decision: "block" | "review";
        reasons: string[];
        projectAddress: string | null;
        clientEmail: string | null;
        clientPhone: string | null;
      }>;
    }>>(`${API_BASE_URL}/v1/public/quote-requests/check-duplicate`, {
      method: "POST",
      headers: authHeaders(),
      body: payload,
    });
    return response.data;
  },

  async createQuoteRequest(payload: QuoteRequestPayload) {
    const response = await coreClient.request<ApiEnvelope<{
      referralLeadId: number;
      referralCode: string;
      duplicateDecision: "allow" | "review" | "block";
      submittedBy: string;
    }>>(`${API_BASE_URL}/v1/public/quote-requests`, {
      method: "POST",
      headers: authHeaders(),
      body: payload,
    });
    return response.data;
  },

  async uploadFile(file: File) {
    const session = readStoredSession();
    if (!session) {
      throw new Error("Referral portal login is required before uploading files");
    }

    const response = await fetch(`${API_BASE_URL}/v1/public/quote-requests/files`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.token}`,
        "x-draft-file-name": file.name,
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    const body = await response.json();
    if (!response.ok) {
      throw new Error(body?.error?.message || body?.message || `HTTP ${response.status}`);
    }
    return body.data as {
      fileId: string;
      fileName: string;
      contentType: string | null;
      size: number;
      uploadedAt: string | null;
    };
  },
};
